import { z } from 'zod';
import { ADMIN_ROLE_LIST } from '../constants/roles.js';
import { ANNOUNCEMENT_SEVERITY, EMAIL_STATUS_LIST } from '../constants/common.js';
import { cidr, email, name, objectId, optional, pagination, requiredText, slug, text } from './common.js';
import { featuresSchema, graceDays, limitsSchema } from './license.js';

export const planSchema = z.strictObject({
  code: slug,
  name: requiredText(60),
  description: optional(text(300)),
  defaultLimits: limitsSchema,
  defaultFeatures: featuresSchema,
  pricing: z.strictObject({
    monthlyPaise: z.coerce.number().int().min(0),
    yearlyPaise: z.coerce.number().int().min(0),
  }),
  defaultGraceDays: graceDays,
  isActive: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(1000).default(0),
});

export const updatePlanSchema = planSchema.omit({ code: true }).partial();

export const announcementSchema = z
  .strictObject({
    title: requiredText(120),
    body: z.string().trim().min(1).max(2000),
    severity: z.enum(ANNOUNCEMENT_SEVERITY).default('info'),
    audience: z.strictObject({
      all: z.boolean().default(true),
      tenantIds: z.array(objectId).max(500).default([]),
      planCodes: z.array(slug).max(20).default([]),
    }),
    startsAt: z.coerce.date(),
    endsAt: optional(z.coerce.date()),
    dismissible: z.boolean().default(true),
    isActive: z.boolean().default(true),
  })
  .refine((v) => !v.endsAt || v.endsAt > v.startsAt, { message: 'End must be after start', path: ['endsAt'] });

export const createAdminUserSchema = z.strictObject({
  email,
  name,
  role: z.enum(ADMIN_ROLE_LIST),
});

export const updateAdminUserSchema = z.strictObject({
  name: name.optional(),
  role: z.enum(ADMIN_ROLE_LIST).optional(),
  status: z.enum(['active', 'disabled']).optional(),
  ipAllowlist: z.array(cidr).max(50).optional(),
});

export const platformSettingsSchema = z.strictObject({
  reminderDays: z
    .array(z.coerce.number().int().min(0).max(60))
    .max(10)
    .refine((v) => new Set(v).size === v.length, 'Duplicate days'),
  adminCopyEmails: z.array(email).max(10),
  defaultGraceDays: graceDays,
  expiryBannerDays: z.coerce.number().int().min(1).max(60),
  offlineBillingHours: z.coerce.number().int().min(1).max(720),
  emailLogRetentionDays: z.coerce.number().int().min(30).max(3650),
  /** Days after a client's grace period ends before its data is deleted; 0 = keep for ever. */
  dataRetentionDaysAfterExpiry: z.coerce.number().int().min(0).max(3650).default(365),
});

export const clientRetentionSchema = z.strictObject({
  purgeAfterDays: z.coerce.number().int().min(0).max(3650).nullable(),
  reason: z.string().trim().min(3, 'Say why').max(500),
});

export const emailTestSchema = z.strictObject({ to: email });

export const emailListQuery = z.strictObject({
  status: z.enum(EMAIL_STATUS_LIST).optional(),
  template: optional(text(60)),
  tenantId: objectId.optional(),
  q: optional(text(120)),
  ...pagination,
});

export const auditListQuery = z.strictObject({
  action: optional(text(80)),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  ...pagination,
});
